import { useEffect, useId, useState } from 'react';
import { Button } from '../components/Button/Button';
import { RevealableInput } from '../components/RevealableInput/RevealableInput';
import { useChildSignin } from './useAuth';
import './ChildSigninDialog.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Kid-facing sign-in dialog. Three fields:
 *  - parent's email
 *  - kid's display name
 *  - PIN (numeric, 4-8 digits)
 *
 * Separate from `AuthDialog` so the surface is calmer for kids
 * (no email/password tabs, no magic-link, no birth-year prompt).
 * The parent's email is small text — kids may have to ask the
 * parent to spell it.
 */
export function ChildSigninDialog({ isOpen, onClose }: Props): JSX.Element | null {
  const titleId = useId();
  const [parentEmail, setParentEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [pin, setPin] = useState('');
  const signin = useChildSignin();

  useEffect(() => {
    if (!isOpen) {
      setParentEmail('');
      setDisplayName('');
      setPin('');
      signin.reset();
      return;
    }
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !signin.isPending) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmedEmail = parentEmail.trim();
  const trimmedName = displayName.trim();
  const pinValid = pin.length >= 4 && pin.length <= 8 && /^\d+$/.test(pin);
  const canSubmit =
    trimmedEmail.length > 0 && trimmedName.length > 0 && pinValid && !signin.isPending;

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    signin.mutate(
      {
        parentEmail: trimmedEmail,
        displayName: trimmedName,
        pin,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <div
      className="bl-childsignin__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !signin.isPending) onClose();
      }}
    >
      <div
        className="bl-childsignin__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 id={titleId} className="bl-childsignin__title">
          Sign in (kid)
        </h2>
        <p className="bl-childsignin__lead">
          Type your grown-up&rsquo;s email, the display name they picked for you, and your PIN.
        </p>

        <form className="bl-childsignin__form" onSubmit={onSubmit}>
          <label className="bl-childsignin__field">
            <span>Grown-up&rsquo;s email</span>
            <input
              type="email"
              autoComplete="email"
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              required
            />
          </label>
          <label className="bl-childsignin__field">
            <span>Your name</span>
            <input
              type="text"
              autoComplete="username"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={80}
              required
            />
          </label>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- RevealableInput renders a real input; lint rule doesn't trace through the custom wrapper */}
          <label className="bl-childsignin__field">
            <span>PIN</span>
            <RevealableInput
              defaultType="tel"
              inputMode="numeric"
              autoComplete="off"
              pattern="[0-9]*"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              maxLength={8}
              required
            />
          </label>

          {signin.error && (
            <p className="bl-childsignin__error" role="alert">
              {signin.error.message}
            </p>
          )}

          <div className="bl-childsignin__actions">
            <Button type="button" variant="secondary" onClick={onClose} disabled={signin.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {signin.isPending ? 'Signing in…' : 'Sign in'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
